import { useState, useMemo, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Button,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Divider,
  Tooltip,
  FormControlLabel,
} from '@mui/material'
import {
  Assessment,
  TrendingUp,
  People,
  Folder,
  VideoCall,
  Download,
  Description,
  EmojiEvents,
  Calculate,
  Campaign,
  CheckCircle,
  Cancel,
  Warning,
  Replay,
} from '@mui/icons-material'
import { useReopenScoreMutation, useGetScoresQuery } from '../store/api/scoringApi'
import {
  useGetReportsDashboardQuery,
  useLazyExportScoresCsvQuery,
  useLazyExportSummaryTxtQuery,
  useLazyExportSummaryPdfQuery,
  useLazyGetRoundCompletionQuery,
  useLazyGetRankingQuery,
  useGetPublishedRoundsQuery,
  usePublishRoundMutation,
  usePublishMatchMutation,
  useAdvanceToFinalMutation,
  usePublishFinalAnnouncementMutation,
  useSetR1ShortlistMutation,
  type RankingResult,
} from '../store/api/reportsApi'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetRoundsQuery } from '../store/api/roundsApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { useGetLatestBracketQuery } from '../store/api/bracketsApi'
import {
  useLazyAdminDownloadTeamCertificateQuery,
  useLazyAdminDownloadAllCertificatesQuery,
} from '../store/api/achievementsApi'
import { useAppSelector } from '../hooks/redux'
import { useHasRole, usePermission } from '../hooks/usePermission'
import { PageHeader } from '../components/PageHeader'
import { getCardSurfaceSx, getContentPaperSx, getTablePaperSx, getFilterPaperSx } from '../theme/sectionSurfaces'
import { gradientBrand } from '../theme/brand'
import { publishAutoPick } from '../utils/r2'

/**
 * Spec PHẦN 9 — Bảng xếp hạng R1 (dual_claimant_packets).
 *
 * 13 cột:
 *   1. # (rank)         2. Đội            3. ĐH/Trường
 *   4. Hồ sơ 1 — Vai trò    5. HS1 Đơn KK /50    6. HS1 Chứng cứ /50    7. HS1 Tổng /100
 *   8. Hồ sơ 2 — Vai trò    9. HS2 Đơn KK /50   10. HS2 Chứng cứ /50   11. HS2 Tổng /100
 *  12. Tổng /200      13. Video /30 (tie-break)
 *
 * Top 15 đội đầu (rank ≤ 15) được highlight nhẹ — gợi ý ngưỡng publish Bán kết.
 */
function R1DualRankingTable({
  ranking,
  teams,
}: {
  ranking: RankingResult
  teams: Array<{ id: string; competition_status?: string | null }>
}) {
  const roleLabel = (r?: string | null) =>
    r === 'plaintiff' ? 'Nguyên đơn' : r === 'defendant' ? 'Bị đơn' : '—'

  const subAvg = (judges: Array<{ memorial_score: number; evidence_score: number }> | undefined) => {
    if (!judges || judges.length === 0) return { mem: 0, evi: 0 }
    const mem = judges.reduce((a, j) => a + (j.memorial_score || 0), 0) / judges.length
    const evi = judges.reduce((a, j) => a + (j.evidence_score || 0), 0) / judges.length
    return { mem, evi }
  }

  return (
    <TableContainer sx={(t) => getTablePaperSx(t)}>
      <Table size="small" sx={{ minWidth: 1280 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 56 }}>Số thứ tự</TableCell>
            <TableCell sx={{ minWidth: 180 }}><strong>Đội</strong></TableCell>
            <TableCell sx={{ minWidth: 140 }}><strong>ĐH/Trường</strong></TableCell>
            <TableCell sx={{ minWidth: 110 }}>
              <strong>Hồ sơ 1</strong>
              <Typography variant="caption" color="text.secondary" display="block">Vai trò</Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 80 }}>
              <strong>HS1 Đơn KK</strong>
              <Typography variant="caption" color="text.secondary" display="block">/50</Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 80 }}>
              <strong>HS1 CC</strong>
              <Typography variant="caption" color="text.secondary" display="block">/50</Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 90 }}>
              <strong>HS1 Tổng</strong>
              <Typography variant="caption" color="text.secondary" display="block">/100</Typography>
            </TableCell>
            <TableCell sx={{ minWidth: 110 }}>
              <strong>Hồ sơ 2</strong>
              <Typography variant="caption" color="text.secondary" display="block">Vai trò</Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 80 }}>
              <strong>HS2 Đơn KK</strong>
              <Typography variant="caption" color="text.secondary" display="block">/50</Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 80 }}>
              <strong>HS2 CC</strong>
              <Typography variant="caption" color="text.secondary" display="block">/50</Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 90 }}>
              <strong>HS2 Tổng</strong>
              <Typography variant="caption" color="text.secondary" display="block">/100</Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 100 }}>
              <strong>Tổng</strong>
              <Typography variant="caption" color="text.secondary" display="block">/200</Typography>
            </TableCell>
            <TableCell align="right" sx={{ minWidth: 110 }}>
              <strong>Video</strong>
              <Typography variant="caption" color="text.secondary" display="block">/30 (tie-break)</Typography>
            </TableCell>
            <TableCell align="center" sx={{ minWidth: 140 }}>
              <strong>Trạng thái</strong>
              <Typography variant="caption" color="text.secondary" display="block">sau công bố</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {ranking.ranking.map((r) => {
            const sub1 = subAvg(r.submission_1_judges)
            const sub2 = subAvg(r.submission_2_judges)
            const inTop15 = r.rank <= 15
            return (
              <TableRow
                key={r.team_id}
                hover
                sx={{
                  verticalAlign: 'top',
                  ...(inTop15 && {
                    bgcolor: (t) => t.palette.action.hover,
                  }),
                }}
              >
                <TableCell>
                  <Chip
                    label={`#${r.rank}`}
                    size="small"
                    color={r.rank === 1 ? 'warning' : r.rank <= 4 ? 'primary' : inTop15 ? 'info' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{r.team_name}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {r.university || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={roleLabel(r.submission_1_representing_role)}
                    color={r.submission_1_representing_role === 'plaintiff' ? 'primary' : r.submission_1_representing_role === 'defendant' ? 'secondary' : 'default'}
                    variant="outlined"
                    sx={{ fontSize: '0.65rem' }}
                  />
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.3 }}>
                    {r.submission_1_judge_count || 0} GK
                  </Typography>
                </TableCell>
                <TableCell align="right">{sub1.mem.toFixed(1)}</TableCell>
                <TableCell align="right">{sub1.evi.toFixed(1)}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>
                    {(r.submission_1_score ?? 0).toFixed(1)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={roleLabel(r.submission_2_representing_role)}
                    color={r.submission_2_representing_role === 'plaintiff' ? 'primary' : r.submission_2_representing_role === 'defendant' ? 'secondary' : 'default'}
                    variant="outlined"
                    sx={{ fontSize: '0.65rem' }}
                  />
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.3 }}>
                    {r.submission_2_judge_count || 0} GK
                  </Typography>
                </TableCell>
                <TableCell align="right">{sub2.mem.toFixed(1)}</TableCell>
                <TableCell align="right">{sub2.evi.toFixed(1)}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={600}>
                    {(r.submission_2_score ?? 0).toFixed(1)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={700} color="primary.main">
                    {(r.main_score ?? 0).toFixed(1)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    / {ranking.main_score_max ?? 200}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {(r.effective_video_score ?? 0) > 0 ? (
                    <Chip
                      size="small"
                      label={`Σ ${(r.effective_video_score ?? 0).toFixed(1)}`}
                      color={r.tied_with_neighbor ? 'warning' : 'default'}
                      variant={r.tied_with_neighbor ? 'filled' : 'outlined'}
                      sx={{ fontWeight: 600 }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary" fontStyle="italic">
                      —
                    </Typography>
                  )}
                  {r.tied_with_neighbor && (
                    <Typography variant="caption" color="warning.main" display="block">
                      ⚖ Đồng điểm
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {(() => {
                    // Bug fix 2026-05-24: cột Trạng thái cho biết kết quả thực sau
                    // shortlist / publish, không chỉ rank gợi ý. Pull từ
                    // team.competition_status (BE set trong set_r1_shortlist + publish_round_results).
                    //
                    // Update 2026-05-25 (UX): user feedback chỉ muốn 2 trạng thái rõ
                    // ràng "Pass" / "Đã loại". Gom các state pass-through
                    // (r1_shortlisted_for_review, advanced_to_semifinal,
                    // advanced_to_final) → "Pass" để bảng dễ đọc. Tooltip vẫn cho biết
                    // chi tiết stage cụ thể.
                    const team = teams.find((t) => t.id === r.team_id)
                    const cs = team?.competition_status
                    const passStates: Record<string, string> = {
                      r1_shortlisted_for_review: 'Đã vào shortlist top 15 — chờ BTC công bố top 4',
                      advanced_to_semifinal: 'Đã được công bố vào Vòng Bán kết',
                      advanced_to_final: 'Đã được công bố vào Vòng Chung kết',
                    }
                    if (cs && passStates[cs]) {
                      return (
                        <Tooltip title={passStates[cs]} arrow>
                          <Chip size="small" label="Pass" color="success" sx={{ fontWeight: 700 }} />
                        </Tooltip>
                      )
                    }
                    if (cs === 'eliminated') {
                      return (
                        <Tooltip title="Đội bị loại — không vào vòng kế tiếp" arrow>
                          <Chip size="small" label="Đã loại" color="error" sx={{ fontWeight: 700 }} />
                        </Tooltip>
                      )
                    }
                    return (
                      <Typography variant="caption" color="text.secondary" fontStyle="italic">
                        Chưa công bố
                      </Typography>
                    )
                  })()}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default function ReportsPage() {
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const isBtc = usePermission('ASSIGN_CASES')
  const [tab, setTab] = useState<0 | 1>(0)
  const { data, isLoading, isError, error } = useGetReportsDashboardQuery()
  const [exportCsv] = useLazyExportScoresCsvQuery()
  const [exportTxt] = useLazyExportSummaryTxtQuery()
  const [exportPdf] = useLazyExportSummaryPdfQuery()
  const [adminCertTeam, { isFetching: certLoading }] = useLazyAdminDownloadTeamCertificateQuery()
  const [adminCertAll, { isFetching: certAllLoading }] = useLazyAdminDownloadAllCertificatesQuery()
  const [downloadingTeamId, setDownloadingTeamId] = useState<string | null>(null)

  // Toolbar dialog "Xuất chứng chỉ" — cho phép chọn case + (tùy chọn) 1 đội
  const [certDialogOpen, setCertDialogOpen] = useState(false)
  const [certDialogCaseId, setCertDialogCaseId] = useState('')
  const [certDialogTeamId, setCertDialogTeamId] = useState('') // '' = tất cả (ZIP)
  const [getDialogRanking, { data: dialogRanking, isFetching: dialogRankingLoading }] = useLazyGetRankingQuery()
  // 2026-05-25: pre-flight check published_results cho case đang chọn trong
  // cert dialog. Nếu case chưa publish round nào → certificate endpoint sẽ
  // 404 (cert dùng `ranking_snapshot` từ published_results, không phải live
  // ranking). Disable button + show alert rõ ràng thay vì để user bấm rồi
  // nhận lỗi "Tải chứng chỉ thất bại".
  const { data: dialogPublishedRounds, isFetching: dialogPublishedLoading } =
    useGetPublishedRoundsQuery(certDialogCaseId, {
      skip: !certDialogOpen || !certDialogCaseId,
    })
  const dialogCaseHasPublished = (dialogPublishedRounds?.published_round_ids?.length || 0) > 0

  const handleOpenCertDialog = () => {
    setCertDialogCaseId(selectedCaseId || '')
    setCertDialogTeamId('')
    setCertDialogOpen(true)
  }

  // Khi đổi case trong dialog → fetch ranking để biết có những đội nào
  useEffect(() => {
    if (certDialogOpen && certDialogCaseId) {
      getDialogRanking({ case_id: certDialogCaseId, round_id: undefined as any })
      setCertDialogTeamId('')
    }
  }, [certDialogOpen, certDialogCaseId, getDialogRanking])

  const handleCertDialogDownload = async () => {
    if (!certDialogCaseId) return
    try {
      if (certDialogTeamId) {
        const team = dialogRanking?.ranking.find((r) => r.team_id === certDialogTeamId)
        await handleDownloadCertificateForCase(certDialogCaseId, certDialogTeamId, team?.team_name || certDialogTeamId)
      } else {
        await handleDownloadZipForCase(certDialogCaseId)
      }
      setCertDialogOpen(false)
    } catch {
      /* notify đã chạy bên trong helper */
    }
  }

  // Bỏ dấu tiếng Việt + chỉ giữ ASCII alnum/_-/đ→d cho tên file (cross-OS safe).
  // Vd: "Đội Luật Sư Xuất Sắc" → "Doi_Luat_Su_Xuat_Sac"
  const safeFilenameVi = (raw: string): string => {
    if (!raw) return 'file'
    const noDiacritics = raw
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '') // bỏ combining diacritical marks (U+0300..U+036F)
      .replace(/đ/g, 'd')
      .replace(/Đ/g, 'D')
    const cleaned = noDiacritics
      .replace(/[^A-Za-z0-9_-]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 80)
    return cleaned || 'file'
  }

  // Bug fix 2026-05-25: responseHandler endpoint download là `.blob()`, error
  // body cũng trả Blob (JSON-encoded). Trước đây access `e.data?.detail` =
  // undefined → notify rơi vào fallback message generic "Tải chứng chỉ thất bại"
  // → user không hiểu lỗi gì. Cần đọc Blob → text → JSON → detail.
  const extractDownloadError = async (
    e: any,
    fallback: string,
  ): Promise<string> => {
    let detail = e?.data?.detail || e?.error || e?.message
    if (e?.data instanceof Blob) {
      try {
        const text = await e.data.text()
        try {
          const parsed = JSON.parse(text)
          detail = parsed?.detail || parsed?.message || text
        } catch {
          detail = text || detail
        }
      } catch {
        /* ignore */
      }
    }
    if (typeof detail === 'string' && detail.trim()) return detail
    if (Array.isArray(detail)) return detail.map((d: any) => d?.msg || JSON.stringify(d)).join('; ')
    return fallback
  }

  const handleDownloadCertificateForCase = async (caseId: string, teamId: string, teamName: string) => {
    setDownloadingTeamId(teamId)
    try {
      const blob = await adminCertTeam({ caseId, teamId }).unwrap()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Certificate_${safeFilenameVi(teamName)}_${caseId}.pdf`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (e: any) {
      const msg = await extractDownloadError(e, 'Tải chứng chỉ thất bại')
      notify(`Tải chứng chỉ thất bại: ${msg}`, 'error')
      throw e
    } finally {
      setDownloadingTeamId(null)
    }
  }

  const handleDownloadZipForCase = async (caseId: string) => {
    try {
      const blob = await adminCertAll(caseId).unwrap()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const ts = new Date().toISOString().replace(/[-:]/g, '').replace('T', '-').slice(0, 13)
      a.download = `E-Court_Certificates_${caseId}_${ts}.zip`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (e: any) {
      const msg = await extractDownloadError(e, 'Tải ZIP chứng chỉ thất bại')
      notify(`Tải ZIP chứng chỉ thất bại: ${msg}`, 'error')
      throw e
    }
  }

  const handleDownloadCertificate = async (teamId: string, teamName: string) => {
    if (!selectedCaseId) return
    try {
      await handleDownloadCertificateForCase(selectedCaseId, teamId, teamName)
    } catch {
      /* notify đã chạy bên trong */
    }
  }

  const handleDownloadAllCertificates = async () => {
    if (!selectedCaseId) return
    try {
      await handleDownloadZipForCase(selectedCaseId)
    } catch {
      /* notify đã chạy bên trong */
    }
  }

  // Round/case filter for tab 1
  const { data: cases = [] } = useGetCasesQuery()
  const { data: rounds = [] } = useGetRoundsQuery()
  const { data: teams = [] } = useGetTeamsQuery()
  const [selectedCaseId, setSelectedCaseId] = useState('')
  const [selectedRoundId, setSelectedRoundId] = useState<string>('')   // '' = tất cả vòng
  // Spec PHẦN 9: detect dual_claimant_packets case → banner thông tin (R1 aggregate inline)
  const isDualClaimantCase = useMemo(() => {
    const c: any = cases.find((cc: any) => cc.id === selectedCaseId)
    return c?.r1_flow_version === 'dual_claimant_packets'
  }, [cases, selectedCaseId])
  const [getCompletion, { data: completion, isFetching: completionLoading }] =
    useLazyGetRoundCompletionQuery()
  const [getRanking, { data: ranking, isFetching: rankingLoading }] = useLazyGetRankingQuery()
  const [publishRound, { isLoading: publishing }] = usePublishRoundMutation()
  const [publishMatch, { isLoading: publishingMatch }] = usePublishMatchMutation()
  const [advanceToFinal, { isLoading: advancingToFinal }] = useAdvanceToFinalMutation()
  const [publishFinalAnnouncement, { isLoading: publishingFinalAnnouncement }] =
    usePublishFinalAnnouncementMutation()
  const [setR1Shortlist, { isLoading: settingShortlist }] = useSetR1ShortlistMutation()
  const [reopenScore, { isLoading: reopening }] = useReopenScoreMutation()
  // Bug fix 2026-05-25: dùng marker durable từ collection `published_results`
  // (BE endpoint /reports/published-rounds) thay cho inference qua
  // team.competition_status → tránh false-positive khoá nút "Công bố".
  const { data: publishedRoundsData, refetch: refetchPublishedRounds } =
    useGetPublishedRoundsQuery(selectedCaseId, { skip: !selectedCaseId })
  // Phase 6 (2026-05-25): bracket cho R2 per-match publish panel.
  // 2026-05-26 fix: BẮT BUỘC truyền round_id — sau advance_to_final → R3 bracket
  // được tạo → query không có round_id trả "latest" = R3 → panel R2 hiển thị
  // sai (chỉ 2 winners thay vì 4 đội R2). Filter theo selectedRoundId.
  const { data: latestBracket, refetch: refetchBracket } = useGetLatestBracketQuery(
    { case_id: selectedCaseId, round_id: selectedRoundId },
    { skip: !selectedCaseId || !selectedRoundId },
  )
  // Issue 4 (2026-05-25): cơ chế mở — admin chọn winner mode cho Bán kết.
  // by_pair = manual (default) | by_score = auto suggest theo Điểm TB cao hơn.
  const [semifinalWinnerMode, setSemifinalWinnerMode] = useState<'by_pair' | 'by_score'>(
    'by_pair',
  )
  // 2026-05-25: BTC bấm "Công bố lại (force)" — flag để handleConfirmPublish
  // gửi force=true override durable lock + re-sync team.competition_status.
  const [republishForce, setRepublishForce] = useState(false)

  // "Yêu cầu chấm lại" dialog state
  const [reopenDialog, setReopenDialog] = useState<{
    open: boolean
    scoreId: string | null
    judgeName: string
    teamName: string
    submissionType: string
  }>({ open: false, scoreId: null, judgeName: '', teamName: '', submissionType: '' })
  const [reopenReason, setReopenReason] = useState('')

  // 2026-05-26: Theo dõi local các match vừa publish trong session — disable
  // ngay nút "Công bố ... thắng" sau click đầu để chống double-submit /spam.
  // Persistent state (m.status='published' / m.winner_team_id) tới sau khi
  // refetchBracket, nhưng polling 15s + race click → cần guard FE-side.
  const [recentlyPublishedMatchIds, setRecentlyPublishedMatchIds] = useState<Set<string>>(new Set())

  // 2026-05-26: Modal chọn vai NĐ/BĐ cho 2 finalists khi advance_to_final.
  // Open khi admin bấm "Công bố chính thức Vòng Bán kết → Chung kết".
  const [finalRoleDialog, setFinalRoleDialog] = useState<{
    open: boolean
    winners: string[]
    plaintiff: string
    defendant: string
  }>({ open: false, winners: [], plaintiff: '', defendant: '' })

  // 2026-05-27: Modal "Công bố kết quả Vòng CK" — thay window.prompt native.
  const [finalAnnounceDialog, setFinalAnnounceDialog] = useState<{
    open: boolean
    note: string
  }>({ open: false, note: '' })

  // 2026-05-26: Fetch live scores theo case → compute Điểm TB / Tổng / Số GK
  // FE-side cho match panel (BK/CK). Trước đây lấy từ ranking (BE aggregate
  // qua publish-precondition), nếu GK chưa submit hết hoặc round chưa
  // "ready" → ranking trả 0.00. Việc tính trực tiếp từ scores cho phép admin
  // theo dõi tiến độ chấm thực tế.
  const { data: caseScores = [] } = useGetScoresQuery(
    { case_id: selectedCaseId || undefined },
    { skip: !selectedCaseId, pollingInterval: 15000 },
  )
  // Map team_id → {sum, count, avg, draftCount} cho round đang chọn.
  // 2026-05-26 v2: KHÔNG bỏ draft — show điểm tổng + đánh dấu drafts để
  // admin biết tiến độ chấm. User UAT: "0.00 / 0" trông như hardcoded vì
  // judges submit thường rất muộn so với draft.
  const liveTeamScores = useMemo(() => {
    const m = new Map<string, { sum: number; count: number; avg: number; draftCount: number }>()
    if (!selectedRoundId) return m
    for (const s of caseScores as any[]) {
      if (s.round_id !== selectedRoundId) continue
      const ts = Number(s.total_score) || 0
      const isDraft = s.status === 'draft'
      const cur = m.get(s.team_id) || { sum: 0, count: 0, avg: 0, draftCount: 0 }
      // Tổng + count: tính cả draft + submitted nếu có total_score > 0
      if (ts > 0 || !isDraft) {
        cur.sum += ts
        cur.count += 1
        cur.avg = cur.count > 0 ? cur.sum / cur.count : 0
      }
      if (isDraft) cur.draftCount += 1
      m.set(s.team_id, cur)
    }
    return m
  }, [caseScores, selectedRoundId])

  // Publish dialog state
  const [publishOpen, setPublishOpen] = useState(false)
  // Bug fix 2026-05-24: track rounds đã publish trong session để khoá nút "Công bố"
  // ngay sau lần bấm đầu (1-time publish/round). Persist across refresh tự suy ra
  // từ team.competition_status — xem `roundAlreadyPublished` ở dưới.
  const [publishedRoundIds, setPublishedRoundIds] = useState<Set<string>>(new Set())
  const [advancingTeams, setAdvancingTeams] = useState<string[]>([])
  const [publishTitle, setPublishTitle] = useState('')
  const [publishNote, setPublishNote] = useState('')
  const [publishStep, setPublishStep] = useState<'config' | 'preview'>('config')
  const [nextRoundLabel, setNextRoundLabel] = useState('Vòng Bán kết')
  const [scheduleBracketAt, setScheduleBracketAt] = useState('')
  const [scheduleRoleSheetDue, setScheduleRoleSheetDue] = useState('')
  const [scheduleHearingAt, setScheduleHearingAt] = useState('')

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false,
    message: '',
    severity: 'info',
  })
  const notify = (message: string, severity: typeof snackbar.severity) =>
    setSnackbar({ open: true, message, severity })

  const caseRounds = useMemo(
    () => rounds.filter((r: any) => r.case_id === selectedCaseId),
    [rounds, selectedCaseId]
  )

  // 2026-05-25: auto-fetch ranking khi round là semifinal/final + polling
  // 15s để match panel hiển thị điểm GK chấm REALTIME (trước đây admin
  // phải bấm "Tính điểm & Xếp hạng" mới refresh → cặp đấu vẫn 0.00 dù
  // GK đã submit phiếu).
  // 2026-05-26: thêm completion (Tiến độ chấm điểm vòng) cùng cadence —
  // trước đây completion chỉ fetch khi admin bấm nút → "Đang chấm 0/8"
  // freeze dù GK đã submit, trông như block hardcoded.
  useEffect(() => {
    if (!selectedCaseId || !selectedRoundId) return
    const round: any = caseRounds.find((r: any) => r.id === selectedRoundId)
    const rt = round?.round_type
    if (rt !== 'semifinal' && rt !== 'final') return
    // Initial fetch — both ranking + completion
    getRanking({ case_id: selectedCaseId, round_id: selectedRoundId })
    getCompletion({ case_id: selectedCaseId, round_id: selectedRoundId })
    // Polling 15s — đủ tươi cho admin xem điểm, không spam BE
    const id = setInterval(() => {
      getRanking({ case_id: selectedCaseId, round_id: selectedRoundId })
      getCompletion({ case_id: selectedCaseId, round_id: selectedRoundId })
    }, 15000)
    return () => clearInterval(id)
  }, [selectedCaseId, selectedRoundId, caseRounds, getRanking, getCompletion])

  const handleExportCsv = async () => {
    try {
      const blob = await exportCsv().unwrap()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'scores_export.csv'
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (e: any) {
      notify(e?.data?.detail || e?.message || 'Xuất CSV thất bại', 'error')
    }
  }

  const handleExportTxt = async () => {
    try {
      const text = await exportTxt().unwrap()
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'summary.txt'
      a.click()
      URL.revokeObjectURL(url)
    } catch (e: any) {
      notify(e?.data?.detail || e?.message || 'Xuất TXT thất bại', 'error')
    }
  }

  const handleExportPdf = async () => {
    try {
      const blob = await exportPdf().unwrap()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const ts = new Date()
        .toISOString()
        .replace(/[-:]/g, '')
        .replace('T', '-')
        .slice(0, 13)
      a.download = `E-Court_Bao-cao-tong-ket_${ts}.pdf`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch (e: any) {
      notify(e?.data?.detail || e?.message || 'Xuất PDF thất bại', 'error')
    }
  }

  // UAT-TC #15: Tính điểm & Xếp hạng
  const handleCalculateRanking = async () => {
    if (!selectedCaseId) {
      notify('Vui lòng chọn cuộc thi', 'warning')
      return
    }
    try {
      // Kiểm tra giám khảo đã chấm xong (chỉ check khi đã chọn round cụ thể)
      if (selectedRoundId) {
        const c = await getCompletion({ case_id: selectedCaseId, round_id: selectedRoundId }).unwrap()
        if (!c.complete) {
          notify(
            `Mới có ${c.submitted_count}/${c.expected_total} phiếu được gửi — vẫn xếp hạng theo dữ liệu hiện có.`,
            'warning'
          )
        }
      }
      await getRanking({ case_id: selectedCaseId, round_id: selectedRoundId || null }).unwrap()
      notify('Đã tính điểm & cập nhật bảng xếp hạng.', 'success')
    } catch (e: any) {
      notify(e?.data?.detail || e?.message || 'Tính điểm thất bại', 'error')
    }
  }

  // UAT-TC #16 / Bán kết #11 / Chung kết #18: Công bố kết quả
  const handleOpenPublish = () => {
    if (!selectedCaseId || !selectedRoundId) {
      notify('Vui lòng chọn cuộc thi và vòng cụ thể trước khi công bố.', 'warning')
      return
    }
    if (!ranking || ranking.ranking.length === 0) {
      notify('Vui lòng tính điểm xếp hạng trước.', 'warning')
      return
    }
    const round = caseRounds.find((r: any) => r.id === selectedRoundId)
    const roundType = (round as any)?.round_type
    // Auto-pick top N theo round_type — logic ở utils/r2.ts để test được.
    const pick = publishAutoPick(roundType)
    const topN = pick?.topN ?? 0
    const nextLabel = pick?.nextLabel ?? 'Vòng tiếp theo'
    const autoPick = topN > 0 ? ranking.ranking.slice(0, topN).map((r) => r.team_id) : []
    setAdvancingTeams(autoPick)
    setNextRoundLabel(nextLabel)
    setPublishTitle(`Kết quả ${round?.name || selectedRoundId}`)
    setPublishNote('')
    setScheduleBracketAt('')
    setScheduleRoleSheetDue('')
    setScheduleHearingAt('')
    setPublishStep('config')
    setPublishOpen(true)
  }

  const handleConfirmPublish = async () => {
    try {
      const schedule: Record<string, string> = {}
      if (scheduleBracketAt) schedule.bracket_at = scheduleBracketAt
      if (scheduleRoleSheetDue) schedule.role_sheet_due = scheduleRoleSheetDue
      if (scheduleHearingAt) schedule.hearing_at = scheduleHearingAt
      const result = await publishRound({
        case_id: selectedCaseId,
        round_id: selectedRoundId,
        advancing_team_ids: advancingTeams,
        title: publishTitle,
        note: publishNote,
        next_round_label: nextRoundLabel,
        next_round_schedule: Object.keys(schedule).length ? schedule : undefined,
        force: republishForce,
      }).unwrap()
      notify(
        `Đã công bố kết quả. Đã gửi thông báo + email cho ${result.notified_teams} đội.`,
        'success'
      )
      // Bug fix 2026-05-24: khoá nút "Công bố" cho round vừa publish ngay session
      // này (1-time per round). Sau refresh, derived state qua /published-rounds
      // sẽ giữ nguyên trạng thái khoá (durable marker từ `published_results`).
      setPublishedRoundIds((prev) => {
        const next = new Set(prev)
        next.add(selectedRoundId)
        return next
      })
      // Force refetch để FE phản ánh ngay state mới (không phải đợi cache TTL).
      refetchPublishedRounds()
      setPublishOpen(false)
      setRepublishForce(false)
    } catch (e: any) {
      notify(e?.data?.detail || e?.message || 'Công bố kết quả thất bại', 'error')
      setRepublishForce(false)
    }
  }

  // Bug fix 2026-05-25: round được coi là "đã công bố" CHỈ KHI có record
  // trong collection `published_results` (durable marker do BE
  // `publish_round_results` push). Không suy ra từ team.competition_status
  // nữa — status có thể set bởi flow khác (vd setup data, force re-publish
  // vòng kế tiếp, manual fix) → false-positive khoá nhầm nút.
  //
  // `publishedRoundIds` (session) vẫn được giữ để khoá ngay sau khi user vừa
  // bấm "Công bố" (trước khi refetch xong).
  const persistedPublishedRoundIds = useMemo(
    () => new Set(publishedRoundsData?.published_round_ids || []),
    [publishedRoundsData],
  )
  const roundAlreadyPublished = useMemo(() => {
    if (!selectedRoundId || !selectedCaseId) return false
    if (publishedRoundIds.has(selectedRoundId)) return true
    return persistedPublishedRoundIds.has(selectedRoundId)
  }, [selectedRoundId, selectedCaseId, publishedRoundIds, persistedPublishedRoundIds])

  // Tied teams that need tie-break
  const tiedRows = (ranking?.ranking || []).filter((r) => r.tied_with_neighbor)
  const advancingRows = (ranking?.ranking || []).filter((r) =>
    advancingTeams.includes(r.team_id),
  )
  const eliminatedRows = (ranking?.ranking || []).filter(
    (r) => !advancingTeams.includes(r.team_id),
  )
  const canManageResults = useHasRole(['secretary'])
  const isSupport = useHasRole(['support'])
  const canExportReports = canManageResults || isSupport

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="Báo cáo và Phân tích"
        subtitle="Tổng quan điểm số, xếp hạng theo vòng, công bố kết quả."
        icon={<Assessment />}
        mb={3}
        actions={
          canExportReports ? (
          <>
            <Button variant="outlined" color="primary" startIcon={<Download />} onClick={handleExportCsv}>
              Xuất CSV điểm
            </Button>
            <Button variant="contained" color="primary" startIcon={<Download />} onClick={handleExportPdf}>
              Xuất PDF
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<EmojiEvents />}
              onClick={handleOpenCertDialog}
              sx={{
                background: gradientBrand,
                '&:hover': { background: gradientBrand, filter: 'brightness(1.06)' },
              }}
            >
              Xuất chứng chỉ
            </Button>
          </>
          ) : null
        }
      />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Tổng quan" />
        <Tab label="Vòng & Xếp hạng" />
      </Tabs>

      {tab === 0 && (
        <>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {isError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {(error as any)?.data?.detail || 'Không tải được thống kê.'}
            </Alert>
          )}
          {data && (
            <>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={0} sx={(theme) => getCardSurfaceSx(theme)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Cuộc thi
                          </Typography>
                          <Typography variant="h4" fontWeight="bold">{data.total_cases}</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}><Folder /></Avatar>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={0} sx={(theme) => getCardSurfaceSx(theme)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Tổng đội
                          </Typography>
                          <Typography variant="h4" fontWeight="bold">{data.total_teams}</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: 'secondary.main', width: 56, height: 56 }}><People /></Avatar>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={0} sx={(theme) => getCardSurfaceSx(theme)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Phiên courtroom
                          </Typography>
                          <Typography variant="h4" fontWeight="bold">{data.total_courtrooms}</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56 }}><VideoCall /></Avatar>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card elevation={0} sx={(theme) => getCardSurfaceSx(theme)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Điểm TB
                          </Typography>
                          <Typography variant="h4" fontWeight="bold">
                            {data.average_total_score != null ? data.average_total_score : '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Phiếu đã gửi: {data.submitted_scores_count}
                          </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}><TrendingUp /></Avatar>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Paper elevation={0} sx={(theme) => ({ ...getContentPaperSx(theme), p: 3 })}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Bảng xếp hạng tổng (theo điểm TB phiếu đã gửi)
                </Typography>
                <TableContainer sx={(theme) => getTablePaperSx(theme)}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Số thứ tự</TableCell>
                        <TableCell>Đội</TableCell>
                        <TableCell align="right">Điểm TB</TableCell>
                        <TableCell align="right">Số phiếu</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(data.leaderboard || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4}>
                            <Typography color="text.secondary">Chưa có dữ liệu xếp hạng.</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.leaderboard.map((row, idx) => (
                          <TableRow key={row.team_id} hover>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>{row.team_name}</TableCell>
                            <TableCell align="right">
                              <Chip label={row.avg_score} size="small" color="primary" />
                            </TableCell>
                            <TableCell align="right">{row.score_count}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}
        </>
      )}

      {tab === 1 && (
        <>
          {/* Spec PHẦN 9 — banner thông tin cuộc thi dual_claimant_packets.
              Sau khi xoá R1Scorecard (Option A), ranking R1 /200 được tổng hợp
              ngay tại tab này (group by submission_index 1+2). Không còn route
              riêng /r1-review. */}
          {isDualClaimantCase && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                Cuộc thi này dùng flow R1 mới (<code>dual_claimant_packets</code>)
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Mỗi đội có 2 hồ sơ /100 (đại diện nguyên đơn + bị đơn). Bảng dưới
                tổng hợp main_score /200 (sum 2 hồ sơ) + tie-break Video /30 khi
                cần.
              </Typography>
            </Alert>
          )}
          {/* Filter: Cuộc thi + Vòng */}
          <Paper
            elevation={0}
            sx={(t) => ({ ...getFilterPaperSx(t), display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' })}
          >
            <FormControl sx={{ minWidth: 240, flex: 1 }} size="small">
              <InputLabel>Cuộc thi</InputLabel>
              <Select
                value={selectedCaseId}
                label="Cuộc thi"
                onChange={(e) => {
                  setSelectedCaseId(e.target.value)
                  setSelectedRoundId('')
                }}
              >
                {cases.map((c: any) => (
                  <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 220 }} size="small" disabled={!selectedCaseId}>
              <InputLabel>Vòng</InputLabel>
              <Select
                value={selectedRoundId}
                label="Vòng"
                onChange={(e) => setSelectedRoundId(e.target.value)}
              >
                <MenuItem value=""><em>Tổng hợp tất cả vòng</em></MenuItem>
                {caseRounds.map((r: any) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name} ({r.round_type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {canManageResults && (
              <>
                <Button
                  variant="contained"
                  startIcon={<Calculate />}
                  onClick={handleCalculateRanking}
                  disabled={!selectedCaseId || rankingLoading || completionLoading}
                  sx={{ background: gradientBrand, textTransform: 'none' }}
                >
                  {rankingLoading || completionLoading ? 'Đang tính...' : 'Tính điểm & Xếp hạng'}
                </Button>
                {(() => {
                  // 2026-05-25: Bán kết dùng publish-match (per-cặp), KHÔNG dùng
                  // publish-round → ẩn nút này để tránh user bấm nhầm khoá vòng.
                  // 2026-05-27: Chung kết cũng dùng publish-match (1 cặp duy
                  // nhất, KHÔNG có vòng tiếp theo) → tương tự ẩn nút round-publish.
                  // 2026-05-27 v2: nếu BK/CK đã publish hết cặp → ẨN HẲN nút
                  // (thay vì show disabled trơ trẽn) — admin đã làm xong.
                  const round: any = caseRounds.find((r: any) => r.id === selectedRoundId)
                  const isSemifinal = round?.round_type === 'semifinal'
                  const isFinal = round?.round_type === 'final'
                  if (isSemifinal || isFinal) {
                    const matches = (latestBracket?.matches as any[]) || []
                    const allPublished = matches.length > 0 && matches.every(
                      (mm) => mm.status === 'published' || !!mm.winner_team_id,
                    )
                    // Hide entirely khi đã publish hết — không cần hint nữa
                    if (allPublished) return null
                    const label = isFinal
                      ? 'Công bố winner CK (xem bên dưới)'
                      : 'Công bố theo cặp (xem bên dưới)'
                    const tooltip = isFinal
                      ? 'Vòng Chung kết: công bố đội vô địch / á quân theo cặp ở section bên dưới — KHÔNG chuyển vòng tiếp theo (CK là vòng cuối).'
                      : 'Vòng Bán kết: công bố chiến thắng theo từng cặp đấu ở section bên dưới.'
                    return (
                      <Tooltip title={tooltip} arrow>
                        <span>
                          <Button
                            variant="outlined"
                            color="info"
                            startIcon={<Campaign />}
                            disabled
                            sx={{ textTransform: 'none' }}
                          >
                            {label}
                          </Button>
                        </span>
                      </Tooltip>
                    )
                  }
                  return (
                    <>
                      <Tooltip
                        title={
                          roundAlreadyPublished
                            ? 'Vòng này đã công bố kết quả. Dùng "Công bố lại (force)" nếu cần ghi đè danh sách qua vòng / cập nhật trạng thái đội.'
                            : ''
                        }
                        arrow
                      >
                        <span>
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<Campaign />}
                            onClick={handleOpenPublish}
                            disabled={!selectedCaseId || !selectedRoundId || !ranking || roundAlreadyPublished}
                            sx={{ textTransform: 'none' }}
                          >
                            {roundAlreadyPublished ? 'Đã công bố' : 'Công bố kết quả'}
                          </Button>
                        </span>
                      </Tooltip>
                      {roundAlreadyPublished && (
                        <Tooltip
                          title="Override durable lock — đồng bộ lại team.competition_status + email lại tất cả đội. Dùng khi student báo 'không vào được courtroom' / status sai."
                          arrow
                        >
                          <span>
                            <Button
                              variant="outlined"
                              color="warning"
                              startIcon={<Campaign />}
                              onClick={() => {
                                // Sửa flag để handleConfirmPublish gửi force=true.
                                setRepublishForce(true)
                                handleOpenPublish()
                              }}
                              disabled={!selectedCaseId || !selectedRoundId || !ranking}
                              sx={{ textTransform: 'none' }}
                            >
                              Công bố lại (force)
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </>
                  )
                })()}
              </>
            )}
          </Paper>

          {/* Trạng thái chấm điểm của giám khảo.
              C1 (audit 2026-05-22): ẨN cho R1 (memorial) vì R1 dual flow admin
              chấm trực tiếp, không có khái niệm "phiếu GK" như R2/R3.
              Chỉ show cho semifinal/final. */}
          {selectedRoundId && completion && (() => {
            const round = caseRounds.find((r: any) => r.id === selectedRoundId)
            const rt = (round as any)?.round_type
            // 2026-05-27: CK admin nhập trực tiếp qua /scoring section riêng
            // — hide progress card này cho CK (chỉ giữ cho BK).
            return rt === 'semifinal' || rt === 'online_hearing'
          })() && (
            <Paper elevation={0} sx={(t) => ({ ...getContentPaperSx(t), mb: 3 })}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Tiến độ chấm điểm vòng
                </Typography>
                <Chip
                  icon={completion.complete ? <CheckCircle /> : <Cancel />}
                  label={
                    completion.complete
                      ? `Hoàn thành ${completion.submitted_count}/${completion.expected_total}`
                      : `Đang chấm ${completion.submitted_count}/${completion.expected_total}`
                  }
                  color={completion.complete ? 'success' : 'warning'}
                />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Giám khảo</strong></TableCell>
                      <TableCell align="center"><strong>Tiến độ</strong></TableCell>
                      <TableCell align="right"><strong>Đã chấm</strong></TableCell>
                      <TableCell align="right"><strong>Cần chấm</strong></TableCell>
                      <TableCell align="right"><strong>Trạng thái</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {completion.judges.map((j) => {
                      const pct = j.expected > 0 ? (j.done / j.expected) * 100 : 0
                      return (
                        <TableRow key={j.judge_id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {j.judge_name || 'Chưa xác định'}
                            </Typography>
                            {j.judge_email && (
                              <Typography variant="caption" color="text.secondary">
                                {j.judge_email}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ minWidth: 160 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box
                                sx={{
                                  flex: 1,
                                  height: 6,
                                  borderRadius: 1,
                                  bgcolor: 'grey.200',
                                  overflow: 'hidden',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${Math.min(100, pct)}%`,
                                    height: '100%',
                                    bgcolor: j.complete ? 'success.main' : 'warning.main',
                                    transition: 'width 0.2s',
                                  }}
                                />
                              </Box>
                              <Typography variant="caption" fontWeight={600} sx={{ minWidth: 36 }}>
                                {pct.toFixed(0)}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              {j.done}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">
                              {j.expected}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              size="small"
                              label={j.complete ? 'Hoàn tất' : 'Đang chấm'}
                              color={j.complete ? 'success' : 'warning'}
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Phase 6 (2026-05-25): Per-match panel cho Vòng Bán kết. Khi user
              chọn round_type='semifinal', render danh sách cặp đấu thay vì chỉ
              ranking. Mỗi cặp: 2 đội + điểm + nút "Công bố thắng [Team]".
              Yêu cầu UX 2026-05-25: chiến thắng vòng Bán kết quyết định theo
              cặp đấu (publish-match), không theo rank chung.
              2026-05-26: Mở rộng cho Vòng Chung kết — CK có 1 cặp/round,
              publish-match cũng theo flow này (admin xem điểm 2 đội rồi
              bấm công bố winner). */}
          {selectedRoundId && (() => {
            const round: any = caseRounds.find((r: any) => r.id === selectedRoundId)
            return round?.round_type === 'semifinal' || round?.round_type === 'final'
          })() && latestBracket && (
            <Paper elevation={0} sx={(t) => ({ ...getContentPaperSx(t), mb: 3 })}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6" fontWeight={700}>
                  {(() => {
                    const round: any = caseRounds.find((r: any) => r.id === selectedRoundId)
                    return round?.round_type === 'final'
                      ? 'Cặp đấu Chung kết — Công bố winner'
                      : 'Cặp đấu Bán kết — Công bố từng cặp'
                  })()}
                </Typography>
                <Chip
                  size="small"
                  label="Chiến thắng theo cặp đấu (không theo điểm chung)"
                  color="info"
                  variant="outlined"
                />
                {/* 2026-05-26: nút "Công bố chính thức Vòng Bán kết" — UAT đề
                    xuất. Khi cả 2 cặp BK đã publish-match → admin bấm nút này
                    để officially switch case sang Vòng Chung kết (force gọi
                    advance_to_final). Đảm bảo BE chốt cs+final_rank đầy đủ
                    cho cả 2 winners + tạo final bracket. Idempotent — gọi
                    nhiều lần an toàn. CHỈ hiện ở stage='semifinal'. */}
                {canManageResults && (() => {
                  const round: any = caseRounds.find((r: any) => r.id === selectedRoundId)
                  if (round?.round_type !== 'semifinal') return null
                  const matches = (latestBracket.matches as any[]) || []
                  const allPublished = matches.length > 0 && matches.every(
                    (mm) => mm.status === 'published' || !!mm.winner_team_id,
                  )
                  const publishedCount = matches.filter(
                    (mm) => mm.status === 'published' || !!mm.winner_team_id,
                  ).length
                  return (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<Campaign />}
                      disabled={!allPublished || advancingToFinal}
                      onClick={() => {
                        if (!selectedCaseId) return
                        // 2026-05-26: thay window.confirm bằng modal NĐ/BĐ —
                        // admin chỉ định vai trò cho 2 finalists trước khi
                        // tạo bracket CK. Random assign cũ bỏ.
                        const winners = matches
                          .map((mm) => mm.winner_team_id)
                          .filter(Boolean) as string[]
                        if (winners.length !== 2) {
                          notify('Cần đủ 2 winners để mở Chung kết', 'error')
                          return
                        }
                        setFinalRoleDialog({
                          open: true,
                          winners,
                          plaintiff: winners[0],
                          defendant: winners[1],
                        })
                      }}
                      sx={{ textTransform: 'none', fontWeight: 700, ml: 'auto' }}
                    >
                      {advancingToFinal
                        ? 'Đang công bố...'
                        : allPublished
                          ? 'Công bố chính thức Vòng Bán kết → Chung kết'
                          : `Cần publish đủ ${publishedCount}/${matches.length} cặp trước`}
                    </Button>
                  )
                })()}
                {/* 2026-05-27: nút "Công bố kết quả Vòng Chung kết" — formal
                    close + broadcast email tổng kết cho TẤT CẢ đội trong case
                    (Vô địch + Á quân + others). Click sau khi đã publish-match
                    CK (chọn winner). Idempotent — gọi nhiều lần re-send. */}
                {canManageResults && (() => {
                  const round: any = caseRounds.find((r: any) => r.id === selectedRoundId)
                  if (round?.round_type !== 'final') return null
                  const matches = (latestBracket.matches as any[]) || []
                  const ckMatch = matches[0]
                  const hasWinner = !!ckMatch?.winner_team_id
                  return (
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<Campaign />}
                      disabled={!hasWinner || publishingFinalAnnouncement}
                      onClick={() => {
                        if (!selectedCaseId || !ckMatch) return
                        setFinalAnnounceDialog({ open: true, note: '' })
                      }}
                      sx={{ textTransform: 'none', fontWeight: 700, ml: 'auto' }}
                    >
                      {publishingFinalAnnouncement
                        ? 'Đang gửi email...'
                        : hasWinner
                          ? '🏆 Công bố kết quả Vòng Chung kết'
                          : 'Cần chọn winner CK trước'}
                    </Button>
                  )
                })()}
              </Box>
              {/* Issue 4 (2026-05-25): cơ chế mở — admin chọn winner mode.
                  "Theo điểm": auto suggest winner = team có average_score cao
                  hơn, button "Áp dụng theo điểm" public với click 1 phát.
                  "Theo cặp" (default): admin click thủ công mỗi cặp. */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ mb: 2 }}
                alignItems={{ sm: 'center' }}
              >
                <FormControl size="small" sx={{ minWidth: 240 }}>
                  <InputLabel>Chọn winner theo</InputLabel>
                  <Select
                    label="Chọn winner theo"
                    value={semifinalWinnerMode}
                    onChange={(e) =>
                      setSemifinalWinnerMode(e.target.value as 'by_pair' | 'by_score')
                    }
                  >
                    <MenuItem value="by_pair">Theo cặp (BTC chọn thủ công)</MenuItem>
                    <MenuItem value="by_score">Theo điểm (auto suggest)</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                  {semifinalWinnerMode === 'by_score'
                    ? 'Hệ thống đề xuất winner = đội có TỔNG điểm cao hơn. BTC vẫn bấm xác nhận để công bố.'
                    : 'BTC chọn thủ công winner cho từng cặp dựa trên nhận xét / verdict.'}
                </Typography>
              </Stack>
              <Alert severity="info" sx={{ mb: 2 }}>
                Vòng Bán kết: mỗi cặp đấu được công bố độc lập. Hệ thống sẽ tự
                chuyển sang Vòng Chung kết khi cả 2 cặp đã có winner.
              </Alert>
              {(latestBracket.matches || []).length === 0 ? (
                <Alert severity="warning">
                  Chưa bốc thăm cặp đấu. Sang trang <b>Bốc thăm</b> để bốc cặp Bán kết.
                </Alert>
              ) : (
                <Stack spacing={2}>
                  {(latestBracket.matches || []).map((m: any, idx: number) => {
                    const teamP: any = teams.find((t) => t.id === m.team_plaintiff_id)
                    const teamD: any = teams.find((t) => t.id === m.team_defendant_id)
                    const rankP = ranking?.ranking?.find((r) => r.team_id === m.team_plaintiff_id)
                    const rankD = ranking?.ranking?.find((r) => r.team_id === m.team_defendant_id)
                    // 2026-05-26: live aggregate trực tiếp từ scores collection
                    // để fallback khi BE ranking trả 0 (chưa đủ ngưỡng publish).
                    const liveP = liveTeamScores.get(m.team_plaintiff_id)
                    const liveD = liveTeamScores.get(m.team_defendant_id)
                    const published = m.status === 'published' || !!m.winner_team_id
                    const winnerName = m.winner_team_id === m.team_plaintiff_id
                      ? teamP?.name
                      : m.winner_team_id === m.team_defendant_id
                      ? teamD?.name
                      : null
                    const matchId = m.match_id || `${latestBracket.id}::m${idx}`

                    const renderSide = (
                      team: any,
                      rank: any,
                      live: { sum: number; count: number; avg: number; draftCount: number } | undefined,
                      side: 'plaintiff' | 'defendant',
                    ) => {
                      // Ưu tiên ranking (đã trừ late penalty); fallback live.
                      const avgScore = (rank?.average_score ?? null) !== null
                        ? rank.average_score
                        : (live?.avg ?? null)
                      const totalScore = (rank?.total_score ?? null) !== null
                        ? rank.total_score
                        : (live?.sum ?? null)
                      const judgeCount = (rank?.judge_count ?? null) !== null
                        ? rank.judge_count
                        : (live?.count ?? null)
                      const isWinner = published && m.winner_team_id === team?.id
                      return (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            borderColor: isWinner ? 'success.main' : 'divider',
                            borderWidth: isWinner ? 2 : 1,
                            background: isWinner ? 'rgba(46,125,50,0.06)' : 'transparent',
                            flex: 1,
                            minWidth: 240,
                          }}
                        >
                          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                            <Chip
                              size="small"
                              label={side === 'plaintiff' ? 'Nguyên đơn' : 'Bị đơn'}
                              color={side === 'plaintiff' ? 'primary' : 'secondary'}
                              variant="outlined"
                            />
                            <Typography variant="body1" fontWeight={700}>
                              {team?.name || '—'}
                            </Typography>
                            {isWinner && (
                              <Chip
                                size="small"
                                label="Thắng"
                                color="success"
                                icon={<EmojiEvents fontSize="small" />}
                                sx={{ fontWeight: 700 }}
                              />
                            )}
                          </Stack>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {team?.university || ''}
                          </Typography>
                          <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                            {/* 2026-05-26: bỏ "Điểm TB" — quyết định winner BK
                                theo TỔNG điểm (sum tất cả GK), không phải trung
                                bình. Late penalty -20đ trừ trực tiếp vào total. */}
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Tổng điểm
                              </Typography>
                              <Typography variant="body2" fontWeight={700} color="primary">
                                {totalScore != null ? Number(totalScore).toFixed(2) : '—'}
                              </Typography>
                              {!!rank?.late_penalty_points && rank.late_penalty_points > 0 && (
                                <Typography
                                  variant="caption"
                                  color="warning.main"
                                  display="block"
                                  sx={{ lineHeight: 1.2, mt: 0.25 }}
                                >
                                  ({(rank.raw_main_score_total ?? rank.raw_main_score ?? 0).toFixed(2)} − {rank.late_penalty_points}đ phạt)
                                </Typography>
                              )}
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Số GK
                              </Typography>
                              <Typography variant="body2">{judgeCount != null ? judgeCount : '—'}</Typography>
                            </Box>
                          </Stack>
                          {!!rank?.late_penalty_points && rank.late_penalty_points > 0 && (
                            <Chip
                              size="small"
                              color="warning"
                              icon={<Warning fontSize="small" />}
                              label={
                                rank.late_penalty_detail?.minutes_late
                                  ? `Nộp phân vai muộn ${rank.late_penalty_detail.minutes_late} phút − ${rank.late_penalty_points}đ`
                                  : `Nộp phân vai muộn − ${rank.late_penalty_points}đ`
                              }
                              sx={{ mt: 1, height: 22, fontSize: 10, fontWeight: 600 }}
                            />
                          )}
                        </Paper>
                      )
                    }

                    const justPublished = recentlyPublishedMatchIds.has(matchId)
                    const handlePublishMatch = async (winnerTeamId: string) => {
                      if (!selectedCaseId || !selectedRoundId) return
                      // 2026-05-26: guard ngay khi bấm — chống spam click trong khi
                      // request đang in-flight + race condition với polling 15s.
                      if (justPublished || publishingMatch) return
                      const winnerName =
                        winnerTeamId === m.team_plaintiff_id
                          ? teamP?.name
                          : teamD?.name
                      if (
                        !window.confirm(
                          `Công bố ${winnerName} thắng cặp này? Hành động này không thể hoàn tác.`,
                        )
                      ) {
                        return
                      }
                      // Optimistic lock: thêm vào set NGAY trước khi gọi BE —
                      // 2 click liên tiếp đều bị block từ click thứ 2.
                      setRecentlyPublishedMatchIds((prev) => new Set(prev).add(matchId))
                      // Safety net: nếu sau 30s mà bracket.matches[i].status
                      // vẫn chưa thành 'published' (BE silent fail / cache miss),
                      // tự release lock để UI không stuck "Đang công bố".
                      // Polling 15s sẽ pick up state thật nếu BE OK.
                      const stuckTimeout = setTimeout(() => {
                        setRecentlyPublishedMatchIds((prev) => {
                          const next = new Set(prev)
                          next.delete(matchId)
                          return next
                        })
                      }, 30000)
                      try {
                        const res = await publishMatch({
                          case_id: selectedCaseId,
                          round_id: selectedRoundId,
                          match_id: matchId,
                          winner_team_id: winnerTeamId,
                        }).unwrap()
                        notify(
                          (res as any).auto_advanced_to_final
                            ? 'Đã công bố. Cả 2 cặp đã có winner — tự động chuyển sang Vòng Chung kết.'
                            : 'Đã công bố cặp đấu. Chờ cặp còn lại để chuyển vòng.',
                          'success',
                        )
                        refetchBracket()
                        refetchPublishedRounds()
                      } catch (e: any) {
                        // Lỗi → release lock ngay để admin retry.
                        clearTimeout(stuckTimeout)
                        setRecentlyPublishedMatchIds((prev) => {
                          const next = new Set(prev)
                          next.delete(matchId)
                          return next
                        })
                        notify(e?.data?.detail || e?.message || 'Công bố thất bại', 'error')
                      }
                    }

                    return (
                      <Paper
                        key={matchId}
                        elevation={0}
                        sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{ mb: 1.5, flexWrap: 'wrap' }}
                        >
                          <Typography variant="subtitle2" fontWeight={700}>
                            Cặp #{idx + 1}
                          </Typography>
                          {published || justPublished ? (
                            <Chip
                              size="small"
                              label={
                                published
                                  ? `Đã công bố — ${winnerName || 'winner'} thắng`
                                  : 'Đang công bố — chờ BE confirm...'
                              }
                              color="success"
                              icon={<CheckCircle fontSize="small" />}
                            />
                          ) : (
                            <Chip size="small" label="Chưa công bố" color="default" variant="outlined" />
                          )}
                        </Stack>
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          spacing={2}
                          alignItems="stretch"
                        >
                          {renderSide(teamP, rankP, liveP, 'plaintiff')}
                          {renderSide(teamD, rankD, liveD, 'defendant')}
                        </Stack>
                        {canManageResults && !published && !justPublished && (() => {
                          // 2026-05-26: winner suggest theo TỔNG điểm (sum GK).
                          // Trước đây dùng average_score nhưng UX confusing:
                          // 2 GK x 80đ vs 1 GK x 90đ → avg lệch hướng số GK.
                          // Tổng phản ánh đúng "khối lượng điểm" GK ưu ái.
                          const scoreP = rankP?.total_score ?? liveP?.sum ?? null
                          const scoreD = rankD?.total_score ?? liveD?.sum ?? null
                          let scoreWinnerId: string | null = null
                          if (
                            scoreP != null &&
                            scoreD != null &&
                            Math.abs(scoreP - scoreD) > 1e-6
                          ) {
                            scoreWinnerId =
                              scoreP > scoreD ? m.team_plaintiff_id : m.team_defendant_id
                          }
                          const isScoreMode = semifinalWinnerMode === 'by_score'
                          return (
                            <Stack
                              direction="column"
                              spacing={1}
                              sx={{ mt: 2 }}
                            >
                              {isScoreMode && (
                                <Alert
                                  severity={scoreWinnerId ? 'info' : 'warning'}
                                  sx={{ py: 0.5 }}
                                >
                                  {scoreWinnerId ? (
                                    <>
                                      Đề xuất theo điểm:{' '}
                                      <b>
                                        {scoreWinnerId === m.team_plaintiff_id
                                          ? teamP?.name
                                          : teamD?.name}
                                      </b>{' '}
                                      thắng (Tổng điểm cao hơn).
                                    </>
                                  ) : (
                                    'Chưa có điểm hoặc 2 đội đồng điểm — không thể auto, vui lòng chọn thủ công.'
                                  )}
                                </Alert>
                              )}
                              <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Button
                                  size="small"
                                  variant={
                                    isScoreMode && scoreWinnerId === m.team_plaintiff_id
                                      ? 'contained'
                                      : isScoreMode
                                      ? 'outlined'
                                      : 'contained'
                                  }
                                  color="primary"
                                  startIcon={<Campaign />}
                                  disabled={publishingMatch || justPublished || !teamP}
                                  onClick={() => handlePublishMatch(m.team_plaintiff_id)}
                                >
                                  Công bố {teamP?.name || 'Nguyên đơn'} thắng
                                  {isScoreMode && scoreWinnerId === m.team_plaintiff_id && ' ⭐'}
                                </Button>
                                <Button
                                  size="small"
                                  variant={
                                    isScoreMode && scoreWinnerId === m.team_defendant_id
                                      ? 'contained'
                                      : isScoreMode
                                      ? 'outlined'
                                      : 'contained'
                                  }
                                  color="secondary"
                                  startIcon={<Campaign />}
                                  disabled={publishingMatch || justPublished || !teamD}
                                  onClick={() => handlePublishMatch(m.team_defendant_id)}
                                >
                                  Công bố {teamD?.name || 'Bị đơn'} thắng
                                  {isScoreMode && scoreWinnerId === m.team_defendant_id && ' ⭐'}
                                </Button>
                                {isScoreMode && scoreWinnerId && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    startIcon={<EmojiEvents />}
                                    disabled={publishingMatch || justPublished}
                                    onClick={() => handlePublishMatch(scoreWinnerId!)}
                                  >
                                    Áp dụng đề xuất theo điểm
                                  </Button>
                                )}
                              </Stack>
                            </Stack>
                          )
                        })()}
                      </Paper>
                    )
                  })}
                </Stack>
              )}
            </Paper>
          )}

          {/* Bảng xếp hạng theo vòng */}
          {ranking && (
            <Paper elevation={0} sx={(t) => getContentPaperSx(t)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h6" fontWeight={600}>
                  Xếp hạng — {selectedRoundId ? 'theo vòng đã chọn' : 'tổng hợp các vòng'}
                </Typography>
                {ranking.tie_break_applied && (
                  <Alert severity="info" sx={{ width: '100%', mt: 1 }} icon={<Warning fontSize="small" />}>
                    <b>Có đội đồng điểm bài thi chính</b> — đã áp dụng tie-break theo
                    Điều 10.3: <b>so tổng điểm Video giới thiệu</b> (3 tiêu chí
                    Sáng tạo / Trình bày / Chuyên nghiệp, max 30đ). Tổng điểm video
                    được tính từ <b>AVG các GK chấm phiếu Video Sơ loại</b>; fallback
                    về điểm BTC chấm tay nếu chưa có GK nào chấm.
                  </Alert>
                )}
                {canExportReports && (
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={handleDownloadAllCertificates}
                    disabled={!selectedCaseId || certAllLoading || ranking.ranking.length === 0}
                    sx={{ textTransform: 'none' }}
                  >
                    {certAllLoading ? 'Đang tạo ZIP...' : 'Tải tất cả chứng chỉ (ZIP)'}
                  </Button>
                )}
                {/* C3-a (audit 2026-05-22): R1 shortlist — chỉ hiện cho R1 dual.
                    Admin chọn top N (default 15) từ ranking → set status
                    `r1_shortlisted_for_review`. Sau đó BTC review → chốt
                    top 4 qua publishRound. */}
                {canManageResults && ranking.flow === 'dual_claimant_packets' && (
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<EmojiEvents />}
                    disabled={
                      settingShortlist || ranking.ranking.length === 0 || !selectedCaseId
                    }
                    onClick={async () => {
                      const currentCase: any = cases.find((c) => c.id === selectedCaseId)
                      const defaultSize = currentCase?.r1_shortlist_size ?? 15
                      const input = window.prompt(
                        `Nhập số đội vào shortlist R1 (top N). Default: ${defaultSize}.\n` +
                          `Các đội còn lại sẽ chuyển sang trạng thái 'eliminated'.`,
                        String(defaultSize),
                      )
                      if (!input) return
                      const n = parseInt(input, 10)
                      if (isNaN(n) || n <= 0 || n > ranking.ranking.length) {
                        notify(`Số đội không hợp lệ (1-${ranking.ranking.length})`, 'warning')
                        return
                      }
                      const eligible = ranking.ranking
                        .filter((r) => !r.incomplete)
                        .sort((a, b) => (a.rank || 999) - (b.rank || 999))
                      const topN = eligible.slice(0, n).map((r) => r.team_id)
                      const ok = window.confirm(
                        `Lập shortlist ${topN.length} đội đầu (theo điểm chính).\n` +
                          `${ranking.ranking.length - topN.length} đội còn lại → 'eliminated'.\n\n` +
                          `Tiếp tục?`,
                      )
                      if (!ok) return
                      try {
                        const res = await setR1Shortlist({
                          case_id: selectedCaseId,
                          shortlisted_team_ids: topN,
                        }).unwrap()
                        notify(
                          `Đã shortlist ${res.shortlisted_count} đội · ` +
                            `${res.eliminated_count} đội bị eliminate.`,
                          'success',
                        )
                      } catch (e: any) {
                        notify(e?.data?.detail || e?.message || 'Lập shortlist thất bại', 'error')
                      }
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    {settingShortlist ? 'Đang lọc...' : 'Lọc top 15'}
                  </Button>
                )}
              </Box>
              {ranking.flow === 'dual_claimant_packets' ? (
                <R1DualRankingTable ranking={ranking} teams={teams as any} />
              ) : (() => {
              // 2026-05-27: Hide cột "Điểm video (Đ.10.3)" cho CK — Điều 10.3
              // chỉ áp R1 tie-break, không liên quan vòng tranh tụng.
              const _round = caseRounds.find((rr: any) => rr.id === selectedRoundId)
              const _rt = (_round as any)?.round_type
              const showVideoCol = _rt !== 'final'
              return (
              <TableContainer sx={(t) => getTablePaperSx(t)}>
                <Table size="small" sx={{ minWidth: 820 }}>
                  {/* 2026-05-25: bỏ 3 cột Điểm chính / Tổng cộng / Số GK
                      theo yêu cầu user — chi tiết điểm vẫn xem được ở cột
                      "Chi tiết phiếu chấm" (per-judge breakdown). Cột Video
                      giữ vì là tie-break (Điều 10.3) — bỏ cho CK. */}
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 60 }}>Số thứ tự</TableCell>
                      <TableCell sx={{ minWidth: 200 }}><strong>Đội</strong></TableCell>
                      {showVideoCol && (
                        <TableCell sx={{ minWidth: 240 }}>
                          <strong>Điểm video (Đ.10.3)</strong>
                          <Typography variant="caption" display="block" color="text.secondary">
                            GK chấm — tie-break khi điểm chính bằng nhau
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell sx={{ minWidth: 280 }}><strong>Chi tiết phiếu chấm</strong></TableCell>
                      {canExportReports && (
                        <TableCell align="right" sx={{ minWidth: 130 }}><strong>Chứng chỉ</strong></TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ranking.ranking.map((r) => (
                      <TableRow key={r.team_id} hover sx={{ verticalAlign: 'top' }}>
                        <TableCell>
                          <Chip
                            label={`#${r.rank}`}
                            size="small"
                            color={r.rank === 1 ? 'warning' : r.rank <= 3 ? 'primary' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{r.team_name}</Typography>
                        </TableCell>
                        {/* 2026-05-25: bỏ 3 cột Điểm chính / Tổng / Số GK */}
                        {showVideoCol && (
                        <TableCell>
                          {(() => {
                            const judgeAvg = r.video_avg_score ?? 0
                            const judgeCount = r.video_judge_count ?? 0
                            const btcTotal = r.video_tiebreak_total ?? 0
                            const source = r.video_source || 'none'
                            const effective = r.effective_video_score ?? 0

                            if (source === 'none') {
                              return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                  <Typography variant="caption" color="text.secondary" fontStyle="italic">
                                    Chưa có điểm video
                                  </Typography>
                                  {r.tied_with_neighbor && (
                                    <Chip
                                      size="small"
                                      label="⚖ Đồng điểm — cần GK chấm video"
                                      color="warning"
                                      sx={{ height: 18, fontSize: '0.66rem', alignSelf: 'flex-start' }}
                                    />
                                  )}
                                </Box>
                              )
                            }
                            return (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                <Chip
                                  size="small"
                                  label={`Σ ${effective.toFixed(2)} / 30`}
                                  color={r.tied_with_neighbor ? 'warning' : 'primary'}
                                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, alignSelf: 'flex-start' }}
                                />
                                {source === 'judge_avg' && (
                                  <Typography variant="caption" color="text.secondary">
                                    {judgeCount} GK chấm · TB
                                  </Typography>
                                )}
                                {source === 'btc_manual' && (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                                    <Typography variant="caption" color="warning.main" fontStyle="italic">
                                      BTC chấm tay (legacy) — {btcTotal.toFixed(1)}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap' }}>
                                      <Chip size="small" label={`ST ${(r.video_tiebreak?.creativity ?? 0).toFixed(1)}`} sx={{ height: 16, fontSize: '0.6rem' }} />
                                      <Chip size="small" label={`TB ${(r.video_tiebreak?.presentation ?? 0).toFixed(1)}`} sx={{ height: 16, fontSize: '0.6rem' }} />
                                      <Chip size="small" label={`CN ${(r.video_tiebreak?.professionalism ?? 0).toFixed(1)}`} sx={{ height: 16, fontSize: '0.6rem' }} />
                                    </Box>
                                  </Box>
                                )}
                                {source === 'judge_avg' && judgeAvg !== effective && (
                                  <Typography variant="caption" color="text.secondary">
                                    AVG GK: {judgeAvg.toFixed(2)}
                                  </Typography>
                                )}
                              </Box>
                            )
                          })()}
                        </TableCell>
                        )}
                        <TableCell>
                          {(!r.judge_details || r.judge_details.length === 0) ? (
                            <Typography variant="caption" color="text.secondary" fontStyle="italic">
                              Chưa có giám khảo nào gửi điểm chính thức
                            </Typography>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {r.judge_details.map((jd) => {
                                const subLabels: Record<string, string> = {
                                  memorial: 'Đơn KK',
                                  evidence: 'Chứng cứ',
                                  transcript: 'Tranh tụng',
                                  courtroom: 'Phiên xử',
                                }
                                // 2026-05-27: nếu judge_id là synthetic
                                // 'proxy:slug' (admin nhập phiếu giấy GK không
                                // có TK) → humanize slug. BE chưa lookup
                                // proxy_judge_name khi build judge_details.
                                let displayName = jd.judge_name || jd.judge_id
                                if (typeof displayName === 'string' && displayName.startsWith('proxy:')) {
                                  const slug = displayName.replace(/^proxy:/, '').replace(/_/g, ' ').trim()
                                  // Capitalize từng từ
                                  displayName = slug.replace(/\b\w/g, (c) => c.toUpperCase()) + ' (phiếu giấy)'
                                }
                                return (
                                  <Box key={jd.judge_id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                    <Typography variant="caption" fontWeight={600} sx={{ minWidth: 80 }}>
                                      {displayName}:
                                    </Typography>
                                    {jd.submissions.map((sub, i) => (
                                      <Box key={i} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                                        <Chip
                                          size="small"
                                          label={`${subLabels[sub.submission_type] || sub.submission_type || '?'} ${sub.score.toFixed(1)}đ`}
                                          sx={{ height: 18, fontSize: '0.68rem' }}
                                          variant="outlined"
                                        />
                                        {canExportReports && sub.score_id && (
                                          <Button
                                            size="small"
                                            variant="text"
                                            color="warning"
                                            startIcon={<Replay sx={{ fontSize: 12 }} />}
                                            sx={{
                                              minWidth: 0,
                                              p: '0 4px',
                                              fontSize: '0.6rem',
                                              textTransform: 'none',
                                              lineHeight: 1,
                                            }}
                                            onClick={() => {
                                              setReopenDialog({
                                                open: true,
                                                scoreId: sub.score_id!,
                                                judgeName: jd.judge_name,
                                                teamName: r.team_name,
                                                submissionType: subLabels[sub.submission_type] || sub.submission_type || '?',
                                              })
                                              setReopenReason('')
                                            }}
                                            title="Yêu cầu giám khảo chấm lại phiếu này"
                                          >
                                            Chấm lại
                                          </Button>
                                        )}
                                      </Box>
                                    ))}
                                    <Chip
                                      size="small"
                                      label={`= ${jd.judge_total.toFixed(2)}đ`}
                                      color="primary"
                                      sx={{ height: 18, fontSize: '0.68rem', fontWeight: 700 }}
                                    />
                                  </Box>
                                )
                              })}
                            </Box>
                          )}
                        </TableCell>
                        {canExportReports && (
                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<Download fontSize="small" />}
                              onClick={() => handleDownloadCertificate(r.team_id, r.team_name)}
                              disabled={(certLoading && downloadingTeamId === r.team_id)}
                              sx={{ textTransform: 'none' }}
                            >
                              {certLoading && downloadingTeamId === r.team_id ? 'Đang tải...' : 'Tải PDF'}
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              )
              })()}
            </Paper>
          )}
        </>
      )}

      {/* Dialog: Công bố kết quả — 2 step stepper (config → preview) */}
      <Dialog
        open={publishOpen}
        onClose={() => {
          setPublishOpen(false)
          setRepublishForce(false)
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assessment color="primary" />
            <span>Công bố kết quả vòng</span>
            <Chip
              size="small"
              label={publishStep === 'config' ? 'Bước 1/2 — Cấu hình' : 'Bước 2/2 — Xem trước email'}
              color={publishStep === 'config' ? 'primary' : 'success'}
              sx={{ ml: 1 }}
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {publishStep === 'config' && (
            <>
              {republishForce && (
                <Alert severity="error" sx={{ mb: 2 }} icon={<Warning />}>
                  <b>Chế độ Công bố lại (force):</b> sẽ override durable lock của
                  lần publish trước. Hệ thống sẽ ghi đè danh sách đội qua vòng,
                  cập nhật lại <code>team.competition_status</code>, và gửi
                  email + in-app cho TẤT CẢ đội lần thứ 2. Dùng khi student báo
                  "đã công bố nhưng không vào courtroom được" hoặc cần sửa
                  danh sách top 4.
                </Alert>
              )}
              <Alert severity="warning" sx={{ mb: 2 }}>
                <b>Lưu ý:</b> Sau khi công bố, hệ thống sẽ gửi email + thông báo cho TẤT CẢ đội.
                Đội qua vòng nhận Mẫu A (chúc mừng kèm hạng/điểm + lịch trình),
                đội còn lại nhận Mẫu B (cảm ơn — KHÔNG nhấn mạnh hạng).
              </Alert>

              {/* BTC override — bypass publish preconditions (chưa phân GK / phiếu chưa đủ).
                  Hiện cho BTC khi round CHƯA publish trước đó (case republish đã có nút
                  "Công bố lại (force)" tự set flag + show red Alert riêng).
                  BE accept force=true có audit log. */}
              {isBtc && !roundAlreadyPublished && (
                <Alert severity="info" sx={{ mb: 2 }} icon={<Warning />}>
                  <FormControlLabel
                    sx={{ m: 0, alignItems: 'flex-start' }}
                    control={
                      <Checkbox
                        size="small"
                        checked={republishForce}
                        onChange={(e) => setRepublishForce(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <b>BTC override — bỏ qua kiểm tra GK / phiếu điểm</b>
                        <Typography variant="caption" component="div" color="text.secondary">
                          Tick khi vòng chưa phân giám khảo hoặc phiếu chấm chưa đủ
                          nhưng BTC vẫn cần công bố (vd. R1 dual flow admin chấm thẳng,
                          edge case judge mất thiết bị). Hành động được ghi audit log.
                        </Typography>
                      </Box>
                    }
                  />
                </Alert>
              )}

              {tiedRows.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }} icon={<Warning />}>
                  <b>Phát hiện {tiedRows.length} đội đồng điểm</b> — đã áp dụng tie-break
                  Điều 10.3 (Video clip). Hãy đảm bảo BTC đã chấm điểm tie-break trong TeamsPage
                  trước khi công bố.
                </Alert>
              )}

              <Stack spacing={2}>
                <TextField
                  label="Tiêu đề công bố"
                  value={publishTitle}
                  onChange={(e) => setPublishTitle(e.target.value)}
                  fullWidth
                />

                <FormControl fullWidth>
                  <InputLabel>Đội qua vòng (Top {advancingTeams.length})</InputLabel>
                  <Select
                    multiple
                    value={advancingTeams}
                    onChange={(e) => setAdvancingTeams(e.target.value as string[])}
                    label={`Đội qua vòng (Top ${advancingTeams.length})`}
                    renderValue={(sel) =>
                      `${(sel as string[]).length} đội: ` +
                      (sel as string[]).map((id) => teams.find((t) => t.id === id)?.name || id).join(', ')
                    }
                  >
                    {(ranking?.ranking || []).map((r) => (
                      <MenuItem key={r.team_id} value={r.team_id}>
                        <Checkbox checked={advancingTeams.indexOf(r.team_id) > -1} />
                        <ListItemText
                          primary={
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <span>#{r.rank} — {r.team_name}</span>
                              {r.tied_with_neighbor && (
                                <Chip size="small" label="⚖ tie-break" color="warning" />
                              )}
                            </Box>
                          }
                          secondary={
                            `Điểm TB: ${r.average_score?.toFixed(2)}` +
                            (r.video_tiebreak_total
                              ? ` · Video tiebreak: ${r.video_tiebreak_total.toFixed(1)}/30`
                              : '')
                          }
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Divider>Lịch trình vòng tiếp theo (sẽ insert vào email Mẫu A)</Divider>

                <TextField
                  label="Tên vòng tiếp theo"
                  value={nextRoundLabel}
                  onChange={(e) => setNextRoundLabel(e.target.value)}
                  size="small"
                  fullWidth
                />
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' } }}>
                  <TextField
                    label="Bốc thăm cặp đấu"
                    type="datetime-local"
                    value={scheduleBracketAt}
                    onChange={(e) => setScheduleBracketAt(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <TextField
                    label="Hạn nộp Bảng phân vai"
                    type="datetime-local"
                    value={scheduleRoleSheetDue}
                    onChange={(e) => setScheduleRoleSheetDue(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                  <TextField
                    label={`Phiên ${nextRoundLabel}`}
                    type="datetime-local"
                    value={scheduleHearingAt}
                    onChange={(e) => setScheduleHearingAt(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Box>

                <TextField
                  label="Ghi chú thêm (tuỳ chọn)"
                  value={publishNote}
                  onChange={(e) => setPublishNote(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Stack>
            </>
          )}

          {publishStep === 'preview' && (
            <Stack spacing={2}>
              <Alert severity="info">
                <b>Xem trước nội dung sẽ gửi:</b> {advancingRows.length} đội nhận Mẫu A,{' '}
                {eliminatedRows.length} đội nhận Mẫu B.
              </Alert>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="success.main" gutterBottom>
                  ✉️ Mẫu A — gửi cho {advancingRows.length} đội đi tiếp
                </Typography>
                <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif', fontSize: '0.85rem', whiteSpace: 'pre-line' }}>
                  {`Tiêu đề: [Gender Justice Moot Court] Chúc mừng Đội <Tên đội> vào ${nextRoundLabel}!\n\nKính gửi Đội trưởng và các thành viên Đội <Tên đội>,\n\nBan Tổ chức trân trọng chúc mừng Đội <Tên đội> đã xuất sắc đạt\nhạng #<X> với tổng điểm <YY.Y> và chính thức được lựa chọn vào ${nextRoundLabel}.\n\n📅 Lịch trình tiếp theo:\n${scheduleBracketAt ? `   • Bốc thăm cặp đấu: ${new Date(scheduleBracketAt).toLocaleString('vi-VN')}\n` : ''}${scheduleRoleSheetDue ? `   • Nộp Bảng phân vai: trước ${new Date(scheduleRoleSheetDue).toLocaleString('vi-VN')}\n` : ''}${scheduleHearingAt ? `   • Phiên ${nextRoundLabel}: ${new Date(scheduleHearingAt).toLocaleString('vi-VN')}\n` : ''}${!scheduleBracketAt && !scheduleRoleSheetDue && !scheduleHearingAt ? '   • Lịch chi tiết sẽ được BTC công bố.\n' : ''}\nTrân trọng,\nBan Tổ chức Cuộc thi`}
                </Box>
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Đội nhận:</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                    {advancingRows.map((r) => (
                      <Chip
                        key={r.team_id}
                        size="small"
                        label={`#${r.rank} ${r.team_name} (${r.average_score?.toFixed(2)})`}
                        color="success"
                      />
                    ))}
                  </Box>
                </Box>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="info.main" gutterBottom>
                  ✉️ Mẫu B — gửi cho {eliminatedRows.length} đội còn lại
                </Typography>
                <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif', fontSize: '0.85rem', whiteSpace: 'pre-line' }}>
                  {`Tiêu đề: [Gender Justice Moot Court] Cảm ơn Đội <Tên đội> đã tham gia Cuộc thi\n\nKính gửi Đội trưởng và các thành viên Đội <Tên đội>,\n\nBan Tổ chức Cuộc thi chân thành cảm ơn Đội <Tên đội> đã tham gia\nvà hoàn thành vòng thi. ...\n\n(KHÔNG nhấn mạnh thứ hạng — đội có thể tự xem điểm trong workspace)\n\nTrân trọng,\nBan Tổ chức Cuộc thi`}
                </Box>
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Đội nhận:</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                    {eliminatedRows.map((r) => (
                      <Chip
                        key={r.team_id}
                        size="small"
                        label={`#${r.rank} ${r.team_name}`}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              </Paper>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPublishOpen(false)
              setRepublishForce(false)
            }}
          >
            Huỷ
          </Button>
          {publishStep === 'config' ? (
            <Button
              onClick={() => setPublishStep('preview')}
              variant="outlined"
              disabled={advancingTeams.length === 0}
            >
              Xem trước email →
            </Button>
          ) : (
            <>
              <Button onClick={() => setPublishStep('config')}>← Quay lại cấu hình</Button>
              <Button
                onClick={handleConfirmPublish}
                variant="contained"
                color="success"
                disabled={publishing || advancingTeams.length === 0}
                startIcon={<CheckCircle />}
              >
                {publishing ? 'Đang công bố...' : 'Xác nhận công bố & gửi email'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog: Xuất chứng chỉ — chọn case + (tùy chọn) 1 đội cụ thể, hoặc tải toàn bộ ZIP */}
      <Dialog open={certDialogOpen} onClose={() => setCertDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Xuất chứng chỉ điện tử</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="info">
              Chứng chỉ ICFGL × UEL — chọn cuộc thi và (tùy chọn) 1 đội cụ thể.
              Bỏ trống đội = tải toàn bộ ZIP cho cả cuộc thi.
            </Alert>
            <FormControl fullWidth size="small">
              <InputLabel>Cuộc thi *</InputLabel>
              <Select
                value={certDialogCaseId}
                label="Cuộc thi *"
                onChange={(e) => setCertDialogCaseId(e.target.value)}
              >
                <MenuItem value="">— Chọn cuộc thi —</MenuItem>
                {cases.map((c: any) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.title || '— Chưa đặt tên —'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" disabled={!certDialogCaseId || dialogRankingLoading}>
              <InputLabel>Đội thi (để trống = tất cả ZIP)</InputLabel>
              <Select
                value={certDialogTeamId}
                label="Đội thi (để trống = tất cả ZIP)"
                onChange={(e) => setCertDialogTeamId(e.target.value)}
              >
                <MenuItem value="">— Tất cả đội (.zip) —</MenuItem>
                {(dialogRanking?.ranking || []).map((r) => (
                  <MenuItem key={r.team_id} value={r.team_id}>
                    #{r.rank} · {r.team_name} ({r.average_score?.toFixed(2)} đ)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {certDialogCaseId && !dialogRankingLoading && (dialogRanking?.ranking?.length || 0) === 0 && (
              <Alert severity="warning">
                Cuộc thi này chưa có bảng xếp hạng — chưa thể xuất chứng chỉ.
              </Alert>
            )}
            {/* 2026-05-25: pre-flight — cert endpoint dùng published_results,
                không phải live ranking. Nếu chưa publish → 404. */}
            {certDialogCaseId &&
              !dialogPublishedLoading &&
              !dialogCaseHasPublished &&
              (dialogRanking?.ranking?.length || 0) > 0 && (
                <Alert severity="warning" icon={<Warning />}>
                  <b>Cuộc thi này chưa được công bố kết quả vòng nào.</b> Chứng
                  chỉ điện tử chỉ xuất được sau khi BTC bấm <b>"Công bố kết
                  quả"</b> trong tab Vòng &amp; Xếp hạng. Bảng xếp hạng bên dưới
                  là điểm <i>live</i>, không lưu vĩnh viễn vào chứng chỉ.
                </Alert>
              )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCertDialogOpen(false)}>Hủy</Button>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleCertDialogDownload}
            disabled={
              !certDialogCaseId ||
              dialogRankingLoading ||
              dialogPublishedLoading ||
              (dialogRanking?.ranking?.length || 0) === 0 ||
              !dialogCaseHasPublished ||
              certLoading ||
              certAllLoading
            }
            sx={{
              background: gradientBrand,
              '&:hover': { background: gradientBrand, filter: 'brightness(1.06)' },
            }}
          >
            {certLoading || certAllLoading
              ? 'Đang tạo PDF...'
              : certDialogTeamId
                ? 'Tải PDF của đội'
                : 'Tải ZIP toàn bộ'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Yêu cầu chấm lại — Admin reopen 1 phiếu cụ thể */}
      <Dialog
        open={reopenDialog.open}
        onClose={() => setReopenDialog((s) => ({ ...s, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Replay color="warning" />
            <span>Yêu cầu chấm lại phiếu điểm</span>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="warning">
              Phiếu của <b>{reopenDialog.judgeName}</b> chấm cho đội{' '}
              <b>{reopenDialog.teamName}</b> ({reopenDialog.submissionType}) sẽ chuyển
              từ <code>submitted</code> về <code>draft</code>, GK sẽ nhận thông báo và
              phải gửi lại. Hành động được audit log (<code>score.reopen</code>).
            </Alert>
            <TextField
              required
              autoFocus
              label="Lý do yêu cầu chấm lại"
              placeholder="Ví dụ: phát hiện nhập sai 1 tiêu chí; GK báo lỗi mạng giữa phiên..."
              multiline
              minRows={3}
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              error={!reopenReason.trim()}
              helperText={!reopenReason.trim() ? 'Bắt buộc nhập lý do để lưu audit log.' : ''}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenDialog((s) => ({ ...s, open: false }))}>
            Huỷ
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!reopenReason.trim() || reopening}
            onClick={async () => {
              if (!reopenDialog.scoreId) return
              try {
                await reopenScore({
                  id: reopenDialog.scoreId,
                  reason: reopenReason.trim(),
                }).unwrap()
                notify('Đã gửi yêu cầu chấm lại — GK nhận thông báo.', 'success')
                setReopenDialog({ open: false, scoreId: null, judgeName: '', teamName: '', submissionType: '' })
                // Refresh ranking + completion
                if (selectedCaseId) {
                  getRanking({ case_id: selectedCaseId, round_id: selectedRoundId || null })
                  if (selectedRoundId) {
                    getCompletion({ case_id: selectedCaseId, round_id: selectedRoundId })
                  }
                }
              } catch (e: any) {
                notify(e?.data?.detail || 'Yêu cầu chấm lại thất bại', 'error')
              }
            }}
          >
            {reopening ? 'Đang gửi...' : 'Xác nhận yêu cầu chấm lại'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2026-05-26: Modal chọn vai NĐ/BĐ cho 2 finalists trước khi
          advance_to_final. Admin phải chỉ định rõ ràng (không random). */}
      <Dialog
        open={finalRoleDialog.open}
        onClose={() => !advancingToFinal && setFinalRoleDialog((s) => ({ ...s, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Phân vai Nguyên đơn / Bị đơn — Vòng Chung kết
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Chọn vai trò cho 2 đội thắng Bán kết. Sau khi bấm xác nhận:
            <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
              <li>Đóng Vòng Bán kết, mở Vòng Chung kết</li>
              <li>Tạo bracket Chung kết với 2 đội theo vai đã chọn</li>
              <li>Email + in-app thông báo vai trò cho 2 đội</li>
              <li>Không thể hoàn tác (cần BTC mở lại thủ công nếu cần)</li>
            </ul>
          </Alert>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="plaintiff-label">Nguyên đơn</InputLabel>
              <Select
                labelId="plaintiff-label"
                label="Nguyên đơn"
                value={finalRoleDialog.plaintiff}
                onChange={(e) => {
                  const newPlaintiff = e.target.value as string
                  setFinalRoleDialog((s) => ({
                    ...s,
                    plaintiff: newPlaintiff,
                    // Auto-set defendant = đội còn lại
                    defendant: s.winners.find((tid) => tid !== newPlaintiff) || '',
                  }))
                }}
              >
                {finalRoleDialog.winners.map((tid) => {
                  const t = teams.find((tt) => tt.id === tid)
                  return (
                    <MenuItem key={tid} value={tid}>
                      {t?.name || tid}
                      {t?.university ? ` — ${t.university}` : ''}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="defendant-label">Bị đơn (tự động)</InputLabel>
              <Select
                labelId="defendant-label"
                label="Bị đơn (tự động)"
                value={finalRoleDialog.defendant}
                disabled
              >
                {finalRoleDialog.winners.map((tid) => {
                  const t = teams.find((tt) => tt.id === tid)
                  return (
                    <MenuItem key={tid} value={tid}>
                      {t?.name || tid}
                      {t?.university ? ` — ${t.university}` : ''}
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setFinalRoleDialog((s) => ({ ...s, open: false }))}
            disabled={advancingToFinal}
          >
            Huỷ
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={
              advancingToFinal ||
              !finalRoleDialog.plaintiff ||
              !finalRoleDialog.defendant ||
              finalRoleDialog.plaintiff === finalRoleDialog.defendant
            }
            onClick={async () => {
              if (!selectedCaseId) return
              try {
                const res = await advanceToFinal({
                  case_id: selectedCaseId,
                  force: true,
                  plaintiff_team_id: finalRoleDialog.plaintiff,
                  defendant_team_id: finalRoleDialog.defendant,
                }).unwrap()
                notify(
                  res.idempotent
                    ? 'Đã chuyển sang Chung kết trước đó. Cập nhật state OK.'
                    : `Đã công bố chính thức Bán kết. ${res.finalists?.length || 2} đội vào Chung kết.`,
                  'success',
                )
                refetchBracket()
                refetchPublishedRounds()
                setFinalRoleDialog((s) => ({ ...s, open: false }))
              } catch (e: any) {
                notify(e?.data?.detail || e?.message || 'Công bố chính thức thất bại', 'error')
              }
            }}
          >
            {advancingToFinal ? 'Đang công bố...' : 'Xác nhận & mở Vòng Chung kết'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2026-05-27: Modal "Công bố CHÍNH THỨC kết quả Vòng Chung kết" */}
      <Dialog
        open={finalAnnounceDialog.open}
        onClose={() => !publishingFinalAnnouncement && setFinalAnnounceDialog({ open: false, note: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          🏆 Công bố CHÍNH THỨC kết quả Vòng Chung kết
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Hành động này sẽ gửi email + in-app notification cho <b>2 đội finalist</b>:
            <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
              <li>🏆 <b>Mẫu CK-A — Vô địch</b> cho đội thắng CK</li>
              <li>🥈 <b>Mẫu CK-B — Á quân</b> cho đội thua CK</li>
              <li>Audit log ghi lại</li>
            </ul>
            <Box sx={{ mt: 1, fontSize: 13, color: 'text.secondary' }}>
              ℹ️ KHÔNG gửi cho các đội đã loại R1/R2 (đã nhận email tổng kết
              ở vòng tương ứng). Có thể gọi nhiều lần để re-send (idempotent).
            </Box>
          </Alert>
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            label="Lời nhắn từ BTC (không bắt buộc)"
            placeholder="VD: Cảm ơn các đội đã đồng hành cùng cuộc thi mùa 2026..."
            value={finalAnnounceDialog.note}
            onChange={(e) => setFinalAnnounceDialog((s) => ({ ...s, note: e.target.value }))}
            helperText="Nội dung này sẽ được chèn vào TẤT CẢ email công bố (Vô địch / Á quân / Cảm ơn)."
            disabled={publishingFinalAnnouncement}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setFinalAnnounceDialog({ open: false, note: '' })}
            disabled={publishingFinalAnnouncement}
          >
            Huỷ
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<Campaign />}
            disabled={publishingFinalAnnouncement || !selectedCaseId}
            onClick={async () => {
              if (!selectedCaseId) return
              try {
                const res = await publishFinalAnnouncement({
                  case_id: selectedCaseId,
                  note: finalAnnounceDialog.note.trim() || undefined,
                }).unwrap()
                const totalSent = Object.values(res.sent_counts || {}).reduce((a, b) => a + b, 0)
                notify(
                  `Đã công bố kết quả "${res.case_title}". Gửi notification cho ${totalSent} đội.`,
                  'success',
                )
                setFinalAnnounceDialog({ open: false, note: '' })
              } catch (e: any) {
                notify(e?.data?.detail || e?.message || 'Công bố thất bại', 'error')
              }
            }}
          >
            {publishingFinalAnnouncement ? 'Đang gửi email...' : '🏆 Công bố & Gửi email'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
